import {Suspense, useCallback} from "react";
import {ActivityIndicator, FlatList, Pressable, RefreshControl, StyleSheet, Text, TouchableOpacity, View} from "react-native";
import {router} from "expo-router";
import {useQueryErrorResetBoundary} from "@tanstack/react-query";
import {ErrorBoundary} from "react-error-boundary";
import {Bell, CheckCheck} from "lucide-react-native";
import {useTranslation} from "react-i18next";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {HeaderText} from "@/components/common/HeaderText";
import type {NotificationItem} from "@/data/notification-api";
import {resolveNotificationRoute} from "@/lib/notification-router";
import {useMarkAllNotificationsRead, useMarkNotificationsRead, useNotificationInbox} from "@/data/notification-hooks";

/**
 * The user's notification inbox.
 *
 * NOT org-scoped, and deliberately so: a notification is an artifact of the person, not of an
 * organization, and DM notifications from several orgs share one list. That is also why a deep
 * link whose organization cannot be resolved can always fall back to this route.
 */
export default function NotificationsScreen() {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const {reset} = useQueryErrorResetBoundary();

    return (
        <View style={{flex: 1, backgroundColor: theme.colors.background}}>
            <HeaderText
                title={t("notifications.title")}
                // Unlike the org screens this one can be opened with nothing behind it — from the
                // bell there is always a dashboard to go back to, but a notification tap need not
                // have gone through one.
                onBackPress={() => (router.canGoBack() ? router.back() : router.replace("/"))}
            />
            <ErrorBoundary
                // Resetting the boundary alone would re-render straight back into the cached
                // rejection; resetting the query cache with it is what makes the retry retry.
                onReset={reset}
                fallbackRender={({resetErrorBoundary}) => <InboxError onRetry={resetErrorBoundary}/>}
            >
                <Suspense
                    fallback={
                        <View style={styles.centered}>
                            <ActivityIndicator color={theme.colors.primary}/>
                        </View>
                    }
                >
                    <NotificationList/>
                </Suspense>
            </ErrorBoundary>
        </View>
    );
}

function InboxError({onRetry}: {onRetry: () => void}) {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();

    return (
        <View style={styles.centered}>
            <Text style={[styles.centeredText, {color: theme.colors.textSecondary}]}>
                {t("notifications.loadError")}
            </Text>
            <TouchableOpacity
                onPress={onRetry}
                activeOpacity={0.7}
                style={[styles.retryButton, {borderColor: theme.colors.border}]}
            >
                <Text style={{color: theme.colors.primary, fontWeight: "600"}}>{t("errors.actions.tryAgain")}</Text>
            </TouchableOpacity>
        </View>
    );
}

function NotificationList() {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const inbox = useNotificationInbox();
    const markRead = useMarkNotificationsRead();
    const markAllRead = useMarkAllNotificationsRead();

    const handlePress = useCallback(
        (item: NotificationItem) => {
            // Reading and going somewhere are independent, and stay that way: the row is marked
            // read whether or not it leads anywhere, and it navigates whether or not it was
            // unread. Neither waits on the other — the mutation is fire-and-forget and its result
            // lands in the cache, so the screen does not have to still be here for it to finish.
            if (!item.read) {
                markRead.mutate([item.id]);
            }

            // No fallback to NOTIFICATION_INBOX_ROUTE here, unlike the push-tap handler: this IS
            // the inbox, so an unroutable row would push this screen on top of itself. Doing
            // nothing is the honest answer — the row has already been read.
            const href = resolveNotificationRoute(item.data);
            if (href) {
                router.push(href);
            }
        },
        [markRead]
    );

    if (inbox.items.length === 0) {
        return (
            <View style={styles.centered}>
                <Bell color={theme.colors.textSecondary} size={32}/>
                <Text style={[styles.centeredText, {color: theme.colors.textSecondary, marginTop: 8}]}>
                    {t("notifications.empty")}
                </Text>
            </View>
        );
    }

    return (
        <FlatList
            data={inbox.items}
            keyExtractor={(item) => item.id}
            contentContainerStyle={{padding: 12}}
            refreshControl={
                <RefreshControl
                    refreshing={inbox.isRefreshing}
                    onRefresh={inbox.refresh}
                    tintColor={theme.colors.primary}
                />
            }
            ListHeaderComponent={
                inbox.unreadCount > 0 ? (
                    <TouchableOpacity
                        style={[styles.markAllRow, {borderColor: theme.colors.border}]}
                        activeOpacity={0.7}
                        disabled={markAllRead.isPending}
                        onPress={() => markAllRead.mutate()}
                    >
                        <CheckCheck color={theme.colors.primary} size={18}/>
                        <Text style={{color: theme.colors.primary, fontWeight: "600"}}>
                            {t("notifications.markAllRead")}
                        </Text>
                    </TouchableOpacity>
                ) : null
            }
            onEndReachedThreshold={0.4}
            onEndReached={() => {
                if (inbox.hasMore && !inbox.isLoadingMore) {
                    inbox.loadMore();
                }
            }}
            ListFooterComponent={
                inbox.isLoadingMore ? <ActivityIndicator color={theme.colors.primary} style={styles.footer}/> : null
            }
            renderItem={({item}) => <NotificationRow item={item} onPress={handlePress}/>}
        />
    );
}

function NotificationRow({item, onPress}: {item: NotificationItem; onPress: (item: NotificationItem) => void}) {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();

    // The row's text is rendered here from keys the backend stored, never from text it sent, so
    // switching language re-renders an old notification in the new one.
    const title = t(item.titleKey, item.params);
    const body = t(item.bodyKey, item.params);

    return (
        <Pressable
            onPress={() => onPress(item)}
            style={[
                styles.row,
                {
                    // Unread rows sit on the surface colour with an accent edge; read ones recede
                    // into the background so the difference survives any org's palette.
                    backgroundColor: item.read ? theme.colors.background : theme.colors.surface,
                    borderColor: item.read ? theme.colors.border : theme.colors.primary
                }
            ]}
        >
            <View style={styles.rowText}>
                <Text
                    style={{
                        color: theme.colors.text,
                        fontSize: theme.fontSize.md,
                        fontWeight: item.read ? "500" : "700"
                    }}
                    numberOfLines={1}
                >
                    {title}
                </Text>
                <Text style={{color: theme.colors.textSecondary, fontSize: theme.fontSize.sm}} numberOfLines={2}>
                    {body}
                </Text>
                <Text style={{color: theme.colors.textSecondary, fontSize: theme.fontSize.xs}}>
                    {t("common:date", {
                        // lastEventAt, not created: the list is sorted by it, so showing anything
                        // else dates a bumped row to when it was first written and the order stops
                        // making sense on screen. PocketBase's space-separated stamp, handed to
                        // Date exactly like every other date in this app.
                        value: new Date(item.lastEventAt),
                        formatParams: {
                            value: {dateStyle: "medium", timeStyle: "short"}
                        }
                    })}
                </Text>
            </View>
            {!item.read && (
                <View
                    style={[styles.unreadDot, {backgroundColor: theme.colors.primary}]}
                    accessibilityLabel={t("notifications.unread")}
                />
            )}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    centered: {flex: 1, alignItems: "center", justifyContent: "center", padding: 24},
    centeredText: {textAlign: "center"},
    retryButton: {
        marginTop: 16,
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 12,
        borderWidth: StyleSheet.hairlineWidth
    },
    markAllRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: 12,
        borderRadius: 12,
        borderWidth: StyleSheet.hairlineWidth,
        marginBottom: 8
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        padding: 14,
        borderRadius: 12,
        borderWidth: StyleSheet.hairlineWidth,
        marginBottom: 8
    },
    rowText: {flex: 1, gap: 2},
    unreadDot: {width: 10, height: 10, borderRadius: 5},
    footer: {paddingVertical: 16}
});
